import * as React from 'react'
import { classnames } from '@halo/_utils/classnames'
import './style/login.scss'
import { Well } from '@pushly/aqe/lib/components'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Alert, Input } from 'antd'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { AuthService } from '@halo/services/auth.service'
import { Container } from 'typescript-ioc'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import { getQueryStringParam } from '@halo/_utils/get-query-string'
import { REF_QUERY_PARAM } from '@halo/constants'
import { RefQueryValue } from '@halo/enums/ref-query-value'

interface ILoginForm extends FormComponentProps {}

interface ILoginFormState {
    hasError?: boolean
    errorMessage?: string | React.ReactNode
}

const LoginForm = Form.create<ILoginForm>()(
    class extends React.Component<ILoginForm, ILoginFormState> {
        public state: ILoginFormState = {
            hasError: false,
        }

        protected appState: AppState
        protected appService: AppService
        protected authService: AuthService

        public constructor(props: any) {
            super(props)

            this.appState = Container.get(AppState)
            this.appService = Container.get(AppService)
            this.authService = Container.get(AuthService)

            const expiredKickout = getQueryStringParam(REF_QUERY_PARAM) === RefQueryValue.EXPIRED_SESSION

            this.state = {
                hasError: expiredKickout,
                errorMessage: !expiredKickout ? '' : 'Your current session has expired.',
            }
        }

        public render() {
            return (
                <div className={classnames('login-form')}>
                    <div className={classnames('login-form-wrapper')}>
                        <Well
                            mode="ghost"
                            hideHeader={true}
                            onSubmit={this.handleSubmit}
                            submitOnEnter={true}
                            submitText="Log in"
                            hideCancel={true}
                        >
                            <div className="login-logo" />

                            <Form className="login-form hide-inline-errors">
                                <Form.Item>
                                    {this.props.form.getFieldDecorator('email', {
                                        validateTrigger: 'onBlur',
                                        rules: [
                                            {
                                                type: 'email',
                                                message: 'Email is not a valid email',
                                            },
                                        ],
                                    })(<Input size="large" prefix={<UserOutlined />} placeholder="Email" />)}
                                </Form.Item>
                                <Form.Item>
                                    {this.props.form.getFieldDecorator('password')(
                                        <Input
                                            size="large"
                                            prefix={<LockOutlined />}
                                            type="password"
                                            placeholder="Password"
                                        />,
                                    )}
                                </Form.Item>

                                {this.state.hasError && <Alert type="error" message={this.state.errorMessage} />}
                            </Form>
                        </Well>
                    </div>
                </div>
            )
        }

        protected handleSubmit = async (ev: any) => {
            this.setState({
                hasError: false,
                errorMessage: '',
            })

            let values: any
            const preflightErrors: string[] = []

            try {
                values = await this.props.form.validateFields()

                if (!values.email || values.email === '') {
                    preflightErrors.push('Email is required')
                }
                if (!values.password || values.password === '') {
                    preflightErrors.push('Password is required')
                }
            } catch (error) {
                if (error.email) {
                    preflightErrors.push(error.email.errors.map((err: any) => err.message))
                }
                if (error.password) {
                    preflightErrors.push(error.password.errors.map((err: any) => err.message))
                }
            }

            if (preflightErrors.length > 0) {
                this.setState({
                    hasError: true,
                    errorMessage: preflightErrors.join(', '),
                })

                return
            }

            try {
                return await this.authService.login(values.email, values.password)
            } catch (error) {
                const errStatus = error.response.status
                const errMessage = error.response.data?.message ?? 'An error has occurred.'

                this.setState({
                    hasError: true,
                    errorMessage:
                        errStatus === 429 ? (
                            <div>
                                <div>Pushly was unable to complete your request.</div>
                                <div>{errMessage.replace(/\.$/, '')}.</div>
                            </div>
                        ) : (
                            errMessage
                        ),
                })
            }
        }
    },
)

export class Login extends React.Component {
    public render() {
        return (
            <div className={classnames('page', 'login')}>
                <div className={classnames('page-wrapper')}>
                    <LoginForm />
                </div>
            </div>
        )
    }
}
